import React from 'react';
import { Chip, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Error as ErrorIcon,
  Pending as PendingIcon
} from '@mui/icons-material';

/**
 * Styled Waltero Status Chip with brand-specific colors and styling
 */
const StyledWalteroStatusChip = styled(Chip)(({ theme, walteroStatus }) => {
  const statusStyles = {
    // Success States - Brand-Compliant Sage Green
    online: {
      backgroundColor: theme.palette.waltero.tertiary.sageGreenMedium, // Brand Sage Green #A4BDA8
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    },
    
    success: {
      backgroundColor: theme.palette.waltero.tertiary.sageGreenMedium, // Brand Sage Green #A4BDA8
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    },
    
    active: {
      backgroundColor: theme.palette.waltero.tertiary.sageGreenDark, // Darker Sage Green #6F8F7B for active
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    },
    
    // Warning States - Brand Apricot
    warning: {
      backgroundColor: theme.palette.waltero.secondary.primaryApricot, // Brand Apricot #f18849
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    },
    
    // Error/Critical States - Brand Dark Apricot
    error: {
      backgroundColor: theme.palette.waltero.secondary.darkApricot, // Brand Dark Apricot #c35d2c
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    },
    
    critical: {
      backgroundColor: theme.palette.waltero.secondary.darkApricot, // Brand Dark Apricot #c35d2c
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    },
    
    // Info States - Brand Primary Blue
    info: {
      backgroundColor: theme.palette.waltero.primary.lightBlue, // Brand Light Blue #3d8dae
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    },
    
    // Neutral/Pending States
    pending: {
      backgroundColor: theme.palette.waltero.primary.paleBlue, // Light blue for pending
      color: theme.palette.waltero.primary.coreBlue,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.primary.coreBlue
      }
    },
    
    offline: {
      backgroundColor: theme.palette.waltero.grey[600], // Neutral grey for offline
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    },
    
    inactive: {
      backgroundColor: theme.palette.waltero.grey[400], // Lighter grey for inactive
      color: theme.palette.waltero.white,
      '& .MuiChip-icon': {
        color: theme.palette.waltero.white
      }
    }
  };

  return {
    borderRadius: '6px',
    fontWeight: 500,
    fontSize: '0.875rem',
    fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    textTransform: 'capitalize',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)'
    },
    ...statusStyles[walteroStatus || 'info']
  };
});

/**
 * Status icon mapping - Brand compliant status indicators
 */
const statusIcons = {
  online: OnlineIcon,
  success: OnlineIcon,
  active: OnlineIcon,
  offline: OfflineIcon,
  inactive: OfflineIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  critical: ErrorIcon,
  info: InfoIcon,
  pending: PendingIcon
};

/**
 * WalteroStatusChip - Brand-compliant status indicators using official Waltero colors
 * 
 * A branded chip component for displaying device/system status using the official
 * Waltero brand color palette with proper semantic color mapping:
 * - Success states (online, success, active): Sage Green (#A4BDA8, #6F8F7B)
 * - Warning states: Brand Apricot (#f18849)
 * - Error/Critical states: Brand Dark Apricot (#c35d2c)
 * - Info states: Brand Light Blue (#3d8dae)
 * - Neutral states: Brand greys
 * 
 * @param {Object} props - Component properties
 * @param {string} props.status - Status value ('online', 'success', 'active', 'offline', 'inactive', 'warning', 'error', 'critical', 'info', 'pending')
 * @param {string} [props.label] - Optional custom label (defaults to status)
 * @param {React.ReactNode} [props.icon] - Optional custom icon (overrides default status icon)
 * @param {boolean} [props.showIcon=true] - Whether to show status icon
 * @param {string} [props.size='medium'] - Chip size ('small', 'medium')
 * @param {boolean} [props.clickable=false] - Whether chip is clickable
 * @param {Function} [props.onClick] - Optional click handler
 * @param {Function} [props.onDelete] - Optional delete handler (shows delete icon)
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.sx] - Additional MUI sx props
 */
const WalteroStatusChip = ({
  status,
  label,
  icon,
  showIcon = true,
  size = 'medium',
  clickable = false,
  onClick,
  onDelete,
  className,
  sx,
  ...otherProps
}) => {
  const theme = useTheme();
  
  // Determine the display label
  const displayLabel = label || status;
  
  // Get the appropriate icon
  const StatusIcon = statusIcons[status] || InfoIcon;
  const chipIcon = showIcon ? (icon || <StatusIcon />) : undefined;
  
  // Filter out custom props to avoid DOM warnings
  const { status: _, ...filteredProps } = otherProps;
  
  return (
    <StyledWalteroStatusChip
      walteroStatus={status}
      label={displayLabel}
      icon={chipIcon}
      size={size}
      clickable={clickable}
      onClick={onClick}
      onDelete={onDelete}
      className={className}
      sx={sx}
      {...filteredProps}
    />
  );
};

export default WalteroStatusChip;